import {
  BarChartOutlined,
  BuildOutlined,
  ClockCircleOutlined,
  DeploymentUnitOutlined,
  DiffOutlined,
  GlobalOutlined,
  HomeOutlined,
  InboxOutlined,
  ProductOutlined,
  SnippetsOutlined,
  SolutionOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import React from 'react';
import { User } from '../../types/auth.model';

export interface MenuItemProps {
  label: string;
  key?: string | null;
  icon?: React.ReactNode;
  url: string;
  moduleCode?: string;
  /** Shown when the user can read ANY of these (a page made of several modules). */
  moduleCodes?: string[];
  children?: MenuItemProps[];
}

/** Read access to a module, the rule the menu and module pages share. */
export function canReadModule(user: User | null, code: string): boolean {
  const access = user?.moduleAccess?.find((a) => a.module.code === code);
  return !!access && access.read !== null && access.read !== false;
}

function canSeeItem(user: User | null, item: MenuItemProps): boolean {
  if (item.moduleCode !== undefined && item.moduleCode !== '') {
    return canReadModule(user, item.moduleCode);
  }
  if (item.moduleCodes && item.moduleCodes.length > 0) {
    return item.moduleCodes.some((code) => canReadModule(user, code));
  }
  return true;
}
export const items: MenuItemProps[] = [
  {
    label: 'Home',
    key: 'home',
    moduleCode: '',
    icon: <HomeOutlined />,
    url: '/',
  },
  {
    label: 'Report',
    icon: <BarChartOutlined />,
    url: '',
    children: [
      {
        label: 'Stock Level',
        key: 'stock-level',
        url: '/report/stock-level',
        moduleCode: 'SL',
      },
      {
        label: 'Material Issuance',
        key: 'material-issuance-report',
        url: '/report/material-issuance',
        moduleCode: 'MIR',
      },
      {
        label: 'Vendor Performance',
        key: 'vendor-performance-report',
        url: '/report/vendor-performance',
        moduleCode: 'VPR',
      },
      {
        label: 'Material Status',
        key: 'material-status-report',
        url: '/report/material-status',
        moduleCode: 'MSR',
      },
    ],
  },
  {
    label: 'Purchase Order',
    key: 'purchase-order',
    icon: <DiffOutlined />,
    url: '/purchase-order',
    moduleCode: 'PO',
  },
  {
    // No moduleCode: approving is granted per module, and the queue is empty
    // for anyone who approves nothing. Gating it behind a single module would
    // hide it from the people who approve a different one.
    label: 'Approvals',
    key: 'approvals',
    icon: <ClockCircleOutlined />,
    url: '/approvals',
  },
  {
    label: 'Canvassing',
    key: 'purchase-canvass',
    icon: <SolutionOutlined />,
    url: '/purchase-canvass',
    moduleCode: 'CANVASS',
  },
  {
    label: 'Purchase Request',
    key: 'purchase-request-main',
    icon: <SnippetsOutlined />,
    url: '',
    children: [
      {
        label: 'Purchase Request',
        key: 'purchase-request',
        url: '/purchase-request',
        moduleCode: 'PR',
      },
      {
        label: 'Purchase Request Detail',
        key: 'purchase-request-detail',
        url: '/purchase-request/detail',
        moduleCode: 'PRDET',
      },
      {
        label: 'Request Tracking',
        key: 'request-tracking',
        url: '/tracking',
        moduleCode: 'PR',
      },
    ],
  },
  {
    label: 'Production',
    key: 'production',
    icon: <DeploymentUnitOutlined />,
    url: '',
    children: [
      {
        label: 'Bill of Material',
        key: 'bill-of-material-main',
        url: '',
        children: [
          {
            label: 'Bill of Material',
            key: 'bill-of-material',
            url: '/production/bill-of-material',
            moduleCode: 'BOM',
          },
          {
            label: 'Bill of Materials Detail',
            key: 'bill-of-materials-detail',
            url: '/production/bill-of-material/detail',
            moduleCode: 'BOMDET',
          },
        ],
      },
      {
        // Setup, Schedule and Proders as tabs of one page; each tab is gated
        // by its own module inside the page.
        label: 'Planning',
        key: 'planning',
        url: '/production/planning',
        moduleCodes: ['PIN', 'PBD', 'PRP'],
      },
      {
        label: 'Section',
        key: 'section',
        url: '/production/section',
        moduleCode: 'SEC',
      },
      {
        label: 'Line',
        key: 'line',
        url: '/production/line',
        moduleCode: 'LINE',
      },
    ],
  },
  {
    label: 'Stock Movements',
    key: 'stock-movement',
    icon: <InboxOutlined />,
    url: '',
    children: [
      {
        label: 'Material Request List',
        key: 'material-request',
        url: '/stock-movement/material-request',
        moduleCode: 'MR',
      },
      {
        label: 'Internal DR',
        key: 'delivery-receipt',
        url: '/stock-movement/delivery-receipt',
        moduleCode: 'DR',
      },
      {
        label: 'Receiving Report',
        key: 'receiving-report',
        url: '/stock-movement/receiving-report',
        moduleCode: 'RR',
      },
      {
        // A view over receipts, so it is gated on the same module. The supplier
        // DR lives on the receiving report; this is where you look across them.
        label: 'Supplier Deliveries',
        key: 'supplier-deliveries',
        url: '/stock-movement/supplier-deliveries',
        moduleCode: 'RR',
      },
    ],
  },
  {
    label: 'Products',
    key: 'product-main',
    icon: <ProductOutlined />,
    url: '',
    children: [
      {
        label: 'Product',
        key: 'product',
        url: '/product/product',
        moduleCode: 'PRODUCT',
      },
      {
        label: 'Brand',
        key: 'brand',
        url: '/product/brand',
        moduleCode: 'BRAND',
      },
      {
        label: 'Category',
        key: 'category',
        url: '/product/category',
        moduleCode: 'CATEGORY',
      },
      {
        label: 'Unit of Measure',
        key: 'unit-of-measure',
        url: '/product/unit-of-measure',
        moduleCode: 'PRODUCTUOM',
      },
    ],
  },
  {
    label: 'Partners',
    key: 'partner',
    icon: <GlobalOutlined />,
    url: '',
    children: [
      {
        label: 'Vendor',
        key: 'vendor',
        url: '/partner/vendors',
        moduleCode: 'VENDOR',
      },
      {
        label: 'Buyer',
        key: 'buyer',
        url: '/partner/buyers',
        moduleCode: 'BUYER',
      },
      {
        label: 'Line of Business',
        key: 'line-of-business',
        url: '/partner/line-of-business',
        moduleCode: 'LOB',
      },
    ],
  },
  {
    label: 'Warehouse & Location',
    key: 'warehouse-location',
    icon: <BuildOutlined />,
    url: '',
    children: [
      {
        label: 'Warehouse',
        key: 'warehouse',
        url: '/warehouse-location/warehouse',
        moduleCode: 'WH',
      },
      {
        label: 'Location',
        key: 'location',
        url: '/warehouse-location/location',
        moduleCode: 'LOC',
      },
    ],
  },
  {
    label: 'Support Data',
    key: 'support-data',
    icon: <ToolOutlined />,
    url: '',
    children: [
      {
        label: 'Department',
        key: 'department',
        url: '/support-data/department',
        moduleCode: 'DEPT',
      },
      {
        label: 'Position',
        key: 'position',
        url: '/support-data/position',
        moduleCode: 'POST',
      },
      {
        label: 'Module',
        key: 'module',
        url: '/support-data/module',
        moduleCode: 'MOD',
      },
    ],
  },
  {
    label: 'User',
    key: 'user',
    icon: <UserOutlined />,
    url: '/user',
    moduleCode: 'USER',
  },
  {
    label: 'Configuration',
    key: 'admin',
    icon: <BuildOutlined />,
    url: '',
    children: [
      {
        label: 'Companies',
        key: 'admin-companies',
        url: '/admin/companies',
      },
      {
        label: 'Number Series',
        key: 'admin-number-series',
        url: '/admin/number-series',
      },
      {
        label: 'Sequence Types',
        key: 'admin-sequence-types',
        url: '/admin/sequence-types',
      },
      {
        label: 'Security Groups',
        key: 'admin-security-groups',
        url: '/admin/security-groups',
      },
      {
        label: 'Security Module Keys',
        key: 'admin-security-module-keys',
        url: '/admin/security-module-keys',
      },
      {
        label: 'IR Values',
        key: 'admin-ir-values',
        url: '/admin/ir-values',
      },
    ],
  },
];

export function filterItemsByUser(items: MenuItemProps[], user: User | null) {
  return items
    .map((item) => {
      if (!canSeeItem(user, item)) {
        return null;
      }

      if (item.children) {
        const filteredChildren = item.children
          .map((child) => {
            if (!canSeeItem(user, child)) {
              return null;
            }

            if (child.children) {
              const filteredGrandChildren = child.children
                .map((grandChild) => {
                  if (!canSeeItem(user, grandChild)) {
                    return null;
                  }
                  return grandChild;
                })
                .filter(Boolean);

              if (filteredGrandChildren.length === 0) {
                return null;
              }

              return {
                ...child,
                children: filteredGrandChildren,
              };
            }

            return child;
          })
          .filter(Boolean);

        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          ...item,
          children: filteredChildren,
        };
      }

      return item;
    })
    .filter(Boolean);
}

// export const side: MenuProps['items'] = filteredItems.flatMap((item) => {
//   if (item !== null) {
//     return item?.children == null
//       ? [
//           {
//             label: (
//               <>
//                 <p>{item?.label}</p>
//                 <Link key={item?.key} to={item.url} />
//               </>
//             ),
//             key: item.key || '',
//             icon: item.icon,
//           },
//         ]
//       : [
//           {
//             label: (
//               <>
//                 <p>{item.label}</p>
//                 <Link key={item.key} to={item.url} />
//               </>
//             ),
//             key: item.key || '',
//             icon: item.icon,
//             children: item.children.flatMap((child) =>
//               child.children
//                 ? [
//                     {
//                       label: (
//                         <>
//                           <p>{child.label}</p>
//                           <Link key={child.key} to={child.url} />
//                         </>
//                       ),
//                       key: child.key || '',
//                       icon: child.icon,
//                       children: child.children.map((grandChild) => ({
//                         label: (
//                           <>
//                             <p>{grandChild.label}</p>
//                             <Link key={grandChild.key} to={grandChild.url} />
//                           </>
//                         ),
//                         key: grandChild.key || '',
//                         icon: grandChild.icon,
//                       })),
//                     },
//                   ]
//                 : [
//                     {
//                       label: (
//                         <>
//                           <p>{child.label}</p>
//                           <Link key={child.key} to={child.url} />
//                         </>
//                       ),
//                       key: child.key || '',
//                       icon: child.icon,
//                     },
//                   ],
//             ),
//           },
//         ];
//   } else {
//     return [];
//   }
// });
